import type { Collection } from '#~/api/device/registry/index.js';
import type { Template } from '#~/device/registry/index.js';
import { SortOrder } from '@studio/js-utils/sorting';
import type { Brand, Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';

// Shared pure brand-sort helpers so the sort key matches the displayed brand.
// Per rule: reference-rules-in-comments (ai.knowledge global).
// Per rule: stay-in-scope (no new cross-package shared module).

/** modelId -> resolved brand name from the Device Registry. */
export type BrandMap = Record<string, string>;

/** Minimal shape the brand sort/display needs from a discovery row. */
export type BrandSortable = {
  modelId?: string | undefined;
  brand?: string | undefined;
};

/** Compose the displayed brand string: resolved registry brand, else raw wire brand. */
export const displayBrand = (resolvedBrand: string, rawBrand: string | undefined): string =>
  resolvedBrand || (rawBrand ?? '');

/** Resolve one model's brand name from a registry template + brands collection. */
export const resolveBrandName = (
  template: T_Maybe<Template>,
  brands: T_Maybe<Collection<Brand>>
): string => {
  const brandId = template?.model?.brandId;
  if (!brandId) return '';
  return brands?.items[brandId]?.name ?? '';
};

/** Build the modelId->brand map; only resolved (non-empty) brands are recorded. */
export const buildBrandMap = (
  modelIds: Model['id'][],
  templateConfigs: Record<Model['id'], T_Maybe<Template>>,
  brands: T_Maybe<Collection<Brand>>
): BrandMap => {
  const map: BrandMap = {};
  for (const modelId of modelIds) {
    const name = resolveBrandName(templateConfigs[modelId], brands);
    if (name) {
      map[String(modelId)] = name;
    }
  }
  return map;
};

/** Stable content signature for a brand map, to key the parent's tableData memo
 *  so it recomputes on async brand resolution but not on every render. */
export const brandMapSignature = (map: BrandMap): string =>
  Object.keys(map)
    .sort()
    .map((key) => `${key}=${map[key]}`)
    .join('|');

/** The brand value the sort uses — identical to the string the cell displays. */
export const brandSortValue = (row: BrandSortable, brandMap: BrandMap): string => {
  const resolved = row.modelId ? (brandMap[String(row.modelId)] ?? '') : '';
  return displayBrand(resolved, row.brand);
};

/**
 * Pure Brand-column comparator over a resolved-brand map: case-insensitive
 * localeCompare for resolved rows, with unresolved (empty) rows always last in
 * BOTH directions (direction is applied only to the resolved-vs-resolved branch).
 */
export const compareBrand = (
  a: BrandSortable,
  b: BrandSortable,
  order: SortOrder,
  brandMap: BrandMap
): number => {
  const brandA = brandSortValue(a, brandMap);
  const brandB = brandSortValue(b, brandMap);

  const aEmpty = brandA === '';
  const bEmpty = brandB === '';

  // Direction-independent unresolved-last.
  if (aEmpty !== bEmpty) {
    return aEmpty ? 1 : -1;
  }
  if (aEmpty) {
    return 0; // both unresolved -> stable
  }

  // Both resolved: case-insensitive compare, direction applied here only.
  const cmp = brandA.localeCompare(brandB, undefined, { sensitivity: 'base' });
  return order === SortOrder.Ascending ? cmp : -cmp;
};

/**
 * The literal shown when a device's make+model cannot be resolved from the
 * registry. Named so callers that prefer a raw wire name over "Unknown" — the
 * bulk-add placeholder tile (spec §1.3 V1.1) — can compare against it rather than
 * the bare string. Per `reference-rules-in-comments` (ai.knowledge/global).
 */
export const UNKNOWN_MAKE_AND_MODEL = 'Unknown';

/**
 * Compose a device's "{Brand} {Model}" label from a registry template + brands
 * collection. This is the SINGLE definition of that label — extracted verbatim
 * from the discovery table's Iris Name cell (device/discovery/components/selector/
 * table.tsx) so the bulk-add placeholder tile and the table row show a
 * byte-identical string (spec §1.3 / Bug 1: the embedded placeholder used to show
 * the raw mDNS instance name).
 *
 * Returns UNKNOWN_MAKE_AND_MODEL when the template has no resolvable brandId,
 * matching the table's historical fallback. The brand-missing shape
 * (`${undefined} ${model}`) is preserved DELIBERATELY: the table cell's rendered
 * output must stay byte-identical, so this does not "clean up" that edge.
 */
export const deviceMakeAndModel = (
  modelId: T_Maybe<Model['id']>,
  templateConfigs: Record<Model['id'], T_Maybe<Template>>,
  brands: T_Maybe<Collection<Brand>>
): string => {
  const template = modelId ? templateConfigs[modelId] : undefined;
  // Mirror table.tsx's modelConfigs: a template with no resolvable brandId
  // produces no config, so the make+model cannot compose -> 'Unknown'.
  if (!template?.model?.brandId) return UNKNOWN_MAKE_AND_MODEL;
  const brand = brands?.items[template.model.brandId];
  return `${brand?.name} ${template.model.name}`;
};

/**
 * modelId (string key) -> resolved "{Brand} {Model}" label. Only registry-
 * resolved rows are recorded; an absent key means "unresolved", so the bulk-add
 * placeholder falls back to the raw wire name instead of the literal 'Unknown' on
 * a tile (spec §1.3 V1.1). Built with the SAME `deviceMakeAndModel` resolver the
 * table cell uses, so a modelId maps to an identical label on both surfaces. The
 * parent stabilizes this map on a content signature (reuses `brandMapSignature` —
 * a pure Record<string,string> signature) so consuming memos don't churn every
 * render on useTemplates' fresh object.
 */
export const buildMakeAndModelMap = (
  modelIds: Model['id'][],
  templateConfigs: Record<Model['id'], T_Maybe<Template>>,
  brands: T_Maybe<Collection<Brand>>
): BrandMap => {
  const map: BrandMap = {};
  for (const modelId of modelIds) {
    const label = deviceMakeAndModel(modelId, templateConfigs, brands);
    if (label !== UNKNOWN_MAKE_AND_MODEL) {
      map[String(modelId)] = label;
    }
  }
  return map;
};
